import React from "react";

import classnames from "classnames";
import isIntuit from "Intuit/isIntuit";
import Page from "MobileComparePlans/Page";
import EnrolledPolicyPlansLoader from "QuotePage/Components/EnrolledPolicyPlansLoader";
import { hasPlansBeforeInitialData, getMedicalPlanEnrolledId, getMedicalPlanRenewalId, getPlanDeviationProps, getSavedPlans } from "QuotePage/Selectors";
import { comparePlansPriceBreakdownURL, downloadDecisionPacketURL, quotePageName } from "QuotePage/urls";
import { connect, useSelector } from "react-redux";
import { Link, withRouter } from "react-router-dom";
import { trackStayWithDefaultRenewal } from "Renewal/utils";
import AnalyticsTracking from "Utils/AnalyticsTracking";

import { LinkButton } from "Cobalt/Button";
import { addPlanToCart } from "Common/CartService";
import EnterAgesModal from "Common/EnterAges/Components/EnterAgesModal";
import { isSampleQuote } from "Common/EnterAges/Selectors";
import Icon from "Common/Icon";
import ScrollToTopOnMount from "Common/ScrollToTopOnMount";
import Button from "Theme/Button";

import type { Dispatch } from "redux";
import type { MedicalPlan } from "Utils/types";

import makeBem from "Utils/makeBem";

const bem = makeBem("MobileComparePage");

const ControlBar = ({ onClickBackToQuotePage }) => (
  <div>
    <Button color="link" onClick={onClickBackToQuotePage}>
      <Icon icon="chevron-left" style={{ marginRight: 5 }} /> Back
    </Button>
  </div>
);

const PrintControlBar = ({ savedPlanIDs }) => (
  <div>
    <Button
      color="link"
      href={downloadDecisionPacketURL({
        relative: false,
        searchParams: { plan_ids: savedPlanIDs },
      })}
      target="_blank"
      onClick={() => {
        AnalyticsTracking.trackEvent("print_decision_packet", {
          location: "compare_plans_page",
        });
      }}
    >
      <Icon icon="print" style={{ marginRight: 5 }} flavor="solid" /> Print
    </Button>
  </div>
);

const SelectPlanButton = ({ plan, onStartApplicationClicked, disableButtons }) => {
  const renewalPlanId = useSelector(getMedicalPlanRenewalId);
  return (
    <Button
      size={isIntuit() ? "medium" : undefined}
      disabled={disableButtons}
      fullWidth
      onClick={() => {
        if (plan.id === renewalPlanId) {
          trackStayWithDefaultRenewal({ location: "compare_plans" });
        }
        onStartApplicationClicked(plan, { location: "compare_plans" });
      }}
    >
      Get Started
    </Button>
  );
};
const ButtonArea = ({ onStartApplicationClicked, plan, disableButtons, medicalPlanEnrolledId }) => (
  <div className={bem("ButtonArea")}>
    {medicalPlanEnrolledId != plan.id ? (
      <SelectPlanButton plan={plan} onStartApplicationClicked={onStartApplicationClicked} disableButtons={disableButtons} />
    ) : (
      <div
        className={classnames({
          [bem("enrolled-plan-spacer")]: !isIntuit(),
          [bem("enrolled-plan-spacer-intuit")]: isIntuit(),
        })}
      />
    )}

    {!isIntuit() && (
      <Link to={comparePlansPriceBreakdownURL({ params: { planID: plan.id } })} component={LinkButton} color="gray" fullWidth mt={3}>
        Cost Breakdown
      </Link>
    )}
  </div>
);

const MobileComparePage = ({
  plans,
  onGetPreciseQuote,
  onClickBackToQuotePage,
  onStartApplicationClicked,
  isSampleQuote,
  disableButtons,
  medicalPlanEnrolledId,
  planDeviationProps,
}) => {
  const topHeading = (
    <div>
      <div style={{ display: "inline-block" }}>
        <ControlBar key="controls" onClickBackToQuotePage={onClickBackToQuotePage} />
      </div>
      <div style={{ display: "inline-block", float: "right" }}>
        <PrintControlBar key="print" savedPlanIDs={plans.filter((p) => p).map((p) => p.id)} />
      </div>
    </div>
  );

  return (
    <div style={{ backgroundColor: "white" }}>
      <ScrollToTopOnMount />
      <Page
        plans={plans}
        isSampleQuote={isSampleQuote}
        topHeading={topHeading}
        isInRenewal={medicalPlanEnrolledId === null}
        planDeviationProps={planDeviationProps}
        planButtons={(plan) => (
          <ButtonArea
            isSampleQuote={isSampleQuote}
            onGetPreciseQuote={onGetPreciseQuote}
            onStartApplicationClicked={onStartApplicationClicked}
            plan={plan}
            medicalPlanEnrolledId={medicalPlanEnrolledId}
            disableButtons={disableButtons}
          />
        )}
      />

      <EnterAgesModal />
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  const loadedPlans = [...getSavedPlans(state), ...(ownProps.enrolledPlans || [])].reduce((acc, plan) => {
    if (plan) {
      acc[plan.id] = plan;
    }
    return acc;
  }, {});
  const plans = state.savedPlanIDs.map((planID) => loadedPlans[planID]);
  return {
    plans,
    onClickBackToQuotePage: () => {
      AnalyticsTracking.trackEvent("cta_clicked", {
        location: "compare_page",
        label: "back",
      });

      ownProps.history.push(quotePageName());
    },
    isSampleQuote: isSampleQuote(state),
    disableButtons: hasPlansBeforeInitialData(state),
    medicalPlanEnrolledId: getMedicalPlanEnrolledId(state),
    planDeviationProps: getPlanDeviationProps(state),
  };
};

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
  onStartApplicationClicked: (plan: MedicalPlan, tracking: any = {}) => {
    dispatch(
      addPlanToCart({
        planType: "medical",
        plan,
        tracking,
        shouldClearDentalVision: false,
        continueToCart: true,
        shouldClearLife: false,
      }),
    );
  },
});

const withRedux = connect(mapStateToProps, mapDispatchToProps);

const ConnectedComparePage = withRouter(withRedux(MobileComparePage));

const HydratedComparePage = (props) => (
  <EnrolledPolicyPlansLoader>
    {({ medicalPlans: enrolledPlans }) => <ConnectedComparePage {...props} enrolledPlans={enrolledPlans} />}
  </EnrolledPolicyPlansLoader>
);

export default HydratedComparePage;
